/**
 * Who is signed in.
 *
 * The session lives in an HTTP-only cookie the browser sends by itself, so this
 * holds no token — only the answer to "does the server still recognise us",
 * re-asked on load. That is what makes the session survive a refresh.
 *
 * The answer is a query like any other, which is what lets the rest of the app
 * treat identity the way it treats data: signing in writes the user into the
 * cache, signing out drops every other cached query, so no screen can render
 * the previous account's rows while the next request is still in flight.
 */

import { createContext, useCallback, useContext, useMemo } from "react";
import type { ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, type User } from "./api";
import { keys } from "./queries";

interface AuthState {
  user: User | null;
  loading: boolean;
  setUser: (user: User | null) => void;
  refresh: () => Promise<void>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const { data, isPending } = useQuery({
    queryKey: keys.me,
    // A rejected `/auth/me` is the ordinary signed-out answer, not a failure to
    // report: it resolves to `null` so the query has data rather than an error.
    queryFn: async () => {
      try {
        return await api.me();
      } catch {
        return null;
      }
    },
    // Identity is not something to re-ask for on every mount, but it must be
    // re-asked when the tab comes back — the session can expire while away.
    staleTime: 5 * 60_000,
  });

  const setUser = useCallback(
    (user: User | null) => queryClient.setQueryData(keys.me, user),
    [queryClient],
  );

  const refresh = useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey: keys.me });
  }, [queryClient]);

  const signOut = useCallback(async () => {
    try {
      await api.logout();
    } finally {
      // Order matters, and not for the reason it looks like. An observer is
      // subscribed to the `me` *query object*, not to the cache — so clearing
      // the cache first destroys that object silently, and the `null` written
      // afterwards lands on a replacement nothing is listening to. The result
      // is correct cache state that never reaches React: the app sits on the
      // dashboard until a refresh re-asks `/auth/me`. So the signed-out answer
      // is written first, into the query the observer still holds.
      setUser(null);
      // Then the rest goes: it was all fetched as the previous account, and no
      // screen may render its rows again. By predicate rather than `clear()`,
      // so `me` keeps the `null` just written into it.
      queryClient.removeQueries({ predicate: (query) => query.queryKey[0] !== keys.me[0] });
      // Signing out is a navigation, so it navigates. Leaving the redirect for
      // `RequireAuth` to infer from empty state is what made this fragile in
      // the first place. `replace`, so Back does not return to the dashboard.
      navigate("/sign-in", { replace: true });
    }
  }, [navigate, queryClient, setUser]);

  const value = useMemo(
    () => ({ user: data ?? null, loading: isPending, setUser, refresh, signOut }),
    [data, isPending, setUser, refresh, signOut],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used inside an AuthProvider");
  return context;
}
